import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { 
  Satellite, 
  Globe, 
  Mountain, 
  Building, 
  Zap, 
  Eye,
  Target,
  Navigation,
  Layers3,
  Settings
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface AdvancedMapControlsProps {
  mapInstance: any;
  isOpen: boolean;
  onClose: () => void;
}

export function AdvancedMapControls({ mapInstance, isOpen, onClose }: AdvancedMapControlsProps) {
  const [tiltAngle, setTiltAngle] = useState([45]);
  const [rotationAngle, setRotationAngle] = useState([0]);
  const [buildingsVisible, setBuildingsVisible] = useState(true);
  const [labelsVisible, setLabelsVisible] = useState(true);
  const [enhancedMode, setEnhancedMode] = useState(false);
  const { toast } = useToast();

  const mapTypes = [
    {
      id: 'street_art_satellite',
      name: 'Ultra HD Satellite',
      icon: <Satellite className="w-4 h-4" />,
      description: 'High-resolution satellite imagery optimized for street art discovery',
      badge: 'HD'
    },
    {
      id: 'earth_3d',
      name: 'Google Earth 3D',
      icon: <Globe className="w-4 h-4" />,
      description: 'Google Earth-style 3D terrain with building heights',
      badge: '3D'
    },
    {
      id: 'enhanced_hybrid',
      name: 'Enhanced Hybrid',
      icon: <Mountain className="w-4 h-4" />,
      description: 'Hybrid view with enhanced detail and contrast',
      badge: 'Enhanced'
    },
    {
      id: 'standard_satellite',
      name: 'Standard Satellite',
      icon: <Building className="w-4 h-4" />,
      description: 'Standard Google satellite imagery',
      badge: 'Standard'
    }
  ];

  const activateMapType = (mapTypeId: string, displayName: string) => {
    if (!mapInstance) return;
    
    try {
      if (mapTypeId === 'standard_satellite') {
        mapInstance.setMapTypeId((window as any).google.maps.MapTypeId.SATELLITE);
      } else {
        mapInstance.setMapTypeId(mapTypeId);
      }
      
      toast({
        title: `${displayName} Activated`,
        description: "Map view has been updated with advanced imaging",
      });
    } catch (error) {
      console.error('Error switching map type:', error);
      toast({
        title: "Error",
        description: "Failed to switch map type",
        variant: "destructive",
      });
    }
  };

  const handleTiltChange = (value: number[]) => {
    setTiltAngle(value);
    if (mapInstance) {
      mapInstance.setTilt(value[0]);
      toast({
        title: `Tilt: ${value[0]}°`,
        description: "Viewing angle adjusted for better wall visibility",
      });
    }
  };

  const handleRotationChange = (value: number[]) => {
    setRotationAngle(value);
    if (mapInstance) {
      mapInstance.setHeading(value[0]);
      toast({
        title: `Rotation: ${value[0]}°`,
        description: "Map rotation adjusted",
      });
    }
  };

  const zoomToMaxDetail = () => {
    if (!mapInstance) return;
    
    const currentZoom = mapInstance.getZoom();
    const targetZoom = Math.min(currentZoom + 4, 22);
    mapInstance.setZoom(targetZoom);
    
    toast({
      title: "Maximum Detail",
      description: `Zoomed to level ${targetZoom} for ultra-detailed inspection`,
    });
  };

  const resetToOptimal = () => {
    if (!mapInstance) return;
    
    mapInstance.setZoom(19);
    mapInstance.setTilt(45);
    mapInstance.setHeading(0);
    setTiltAngle([45]);
    setRotationAngle([0]);
    
    toast({
      title: "Optimal View",
      description: "Reset to optimal settings for street art discovery",
    });
  };

  if (!isOpen) return null;

  return (
    <Card className="absolute top-4 right-4 w-80 bg-black/90 backdrop-blur-md border-gray-600 text-white z-40">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Layers3 className="w-4 h-4 text-neon-green" />
            Advanced Imaging Controls
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            data-testid="button-close-controls"
          >
            ×
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <Tabs defaultValue="imagery" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-gray-800">
            <TabsTrigger value="imagery" className="text-xs">Imagery</TabsTrigger>
            <TabsTrigger value="controls" className="text-xs">3D Controls</TabsTrigger>
          </TabsList>
          
          <TabsContent value="imagery" className="space-y-3 mt-3">
            <div className="space-y-2">
              {mapTypes.map((mapType) => (
                <Button
                  key={mapType.id}
                  variant="ghost"
                  className="w-full justify-start p-3 h-auto text-left"
                  onClick={() => activateMapType(mapType.id, mapType.name)}
                  data-testid={`button-${mapType.id}`}
                >
                  <div className="flex items-start gap-3">
                    {mapType.icon}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{mapType.name}</span>
                        <Badge variant="secondary" className="text-xs">
                          {mapType.badge}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {mapType.description}
                      </p>
                    </div>
                  </div>
                </Button>
              ))}
            </div>
            
            <div className="border-t border-gray-700 pt-3 space-y-2">
              <Button
                onClick={zoomToMaxDetail}
                className="w-full bg-neon-green/20 text-neon-green hover:bg-neon-green/30"
                data-testid="button-max-detail"
              >
                <Target className="w-4 h-4 mr-2" />
                Maximum Detail Zoom
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="controls" className="space-y-4 mt-3">
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Tilt Angle</label>
                  <span className="text-xs text-gray-400">{tiltAngle[0]}°</span>
                </div>
                <Slider
                  value={tiltAngle}
                  onValueChange={handleTiltChange}
                  max={85}
                  min={0}
                  step={5}
                  className="w-full"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Rotation</label>
                  <span className="text-xs text-gray-400">{rotationAngle[0]}°</span>
                </div>
                <Slider
                  value={rotationAngle}
                  onValueChange={handleRotationChange}
                  max={360}
                  min={0}
                  step={10}
                  className="w-full"
                />
              </div>
              
              <div className="border-t border-gray-700 pt-3 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm">3D Buildings</label>
                  <Switch
                    checked={buildingsVisible}
                    onCheckedChange={setBuildingsVisible}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm">Enhanced Mode</label>
                  <Switch
                    checked={enhancedMode}
                    onCheckedChange={setEnhancedMode}
                  />
                </div>
              </div>
              
              <Button
                onClick={resetToOptimal}
                variant="outline"
                className="w-full"
                data-testid="button-reset-optimal"
              >
                <Navigation className="w-4 h-4 mr-2" />
                Reset to Optimal
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}